/**
 * M11: the design token system for everything that is drawn in code (PixiJS, SVG).
 * The same values exist as CSS custom properties in styles/tokens.css (the unit test keeps the two in sync),
 * so React components, Tailwind classes and the canvas renderers share one palette, one set of glows,
 * panel/border/icon sizes and one motion vocabulary.
 *
 * Role colours (what a colour MEANS in the game):
 *   survey      cold blue        exploration, survey fleets, scouting information
 *   build       amber            construction, stations, outposts
 *   military    red              warships, combat, hostility
 *   economy     green            resources, production, trade
 *   diplomacy   muted violet     treaties, contacts, mixed fleets
 *   neutral     grey-white       plain information, empty / unknown
 */
import type { StarType } from '@galaxis/shared';

export type Role = 'survey' | 'build' | 'military' | 'economy' | 'diplomacy' | 'neutral';

export interface RoleColor {
  /** CSS colour (#rrggbb) */
  css: string;
  /** Same colour for PixiJS */
  hex: number;
  /** Lighter tone for highlights / text on dark surfaces */
  light: string;
  lightHex: number;
  /** Darker tone for hull shading / pressed states */
  dark: string;
  darkHex: number;
}

const role = (css: string, light: string, dark: string): RoleColor => ({
  css, hex: parseInt(css.slice(1), 16),
  light, lightHex: parseInt(light.slice(1), 16),
  dark, darkHex: parseInt(dark.slice(1), 16),
});

export const ROLE_COLORS: Record<Role, RoleColor> = {
  survey:    role('#38bdf8', '#bae6fd', '#0c4a6e'),
  build:     role('#f59e0b', '#fde68a', '#78350f'),
  military:  role('#ef4444', '#fecaca', '#7f1d1d'),
  economy:   role('#22c55e', '#bbf7d0', '#14532d'),
  diplomacy: role('#a78bfa', '#ddd6fe', '#4c1d95'),
  neutral:   role('#cbd5e1', '#f8fafc', '#334155'),
};

/** Brand accent (links, focus, selection). Unchanged blue of the previous look. */
export const ACCENT = { css: '#4a9eff', hex: 0x4a9eff, hover: '#66b0ff', dim: '#1e4a7a' } as const;

/** Surfaces: deep space → panels. */
export const SURFACE = {
  void: '#030712',
  space: '#0a0f1e',
  panel: 'rgba(10, 15, 30, 0.9)',
  panelRaised: 'rgba(15, 23, 42, 0.92)',
  voidHex: 0x030712,
  spaceHex: 0x0a0f1e,
} as const;

export const BORDER = {
  subtle: 'rgba(148, 163, 184, 0.16)',
  accent: 'rgba(74, 158, 255, 0.28)',
  strong: 'rgba(74, 158, 255, 0.5)',
} as const;

/** Glow strengths (alpha of the soft halo, relative sizes are in the renderers). */
export const GLOW = { faint: 0.08, soft: 0.16, strong: 0.32 } as const;

/** Icon sizes in CSS px (all hit areas stay ≥ 44 px through padding). */
export const ICON_SIZE = { xs: 14, sm: 18, md: 24, lg: 32, xl: 48, hero: 72 } as const;

/** Radii in CSS px. */
export const RADIUS = { sm: 6, md: 10, lg: 14, pill: 999 } as const;

/** Motion vocabulary (ms) — decorative motion is dropped under prefers-reduced-motion. */
export const MOTION = { fast: 120, base: 200, slow: 360, pulse: 2400, engineFlicker: 90 } as const;

// ---- stars ----

export interface StarStyle {
  /** Core colour of the star disc */
  core: number;
  /** Colour of the soft glow / corona */
  glow: number;
  /** Radius of the disc in world units at scale 1 */
  radius: number;
  /** Radius of the glow relative to the disc radius */
  glowScale: number;
  label: string;
}

export const STAR_STYLES: Record<StarType, StarStyle> = {
  yellow_dwarf: { core: 0xfff2b0, glow: 0xffc933, radius: 5,   glowScale: 3.4, label: 'Sárga törpe' },
  red_dwarf:    { core: 0xffb4a8, glow: 0xef5350, radius: 4,   glowScale: 3.0, label: 'Vörös törpe' },
  blue_giant:   { core: 0xe6f6ff, glow: 0x4fc3f7, radius: 7,   glowScale: 4.2, label: 'Kék óriás' },
  white_dwarf:  { core: 0xffffff, glow: 0xdbe4ff, radius: 3.5, glowScale: 3.0, label: 'Fehér törpe' },
  neutron_star: { core: 0xffffff, glow: 0xce93d8, radius: 3,   glowScale: 4.0, label: 'Neutroncsillag' },
  black_hole:   { core: 0x000000, glow: 0xff9a3c, radius: 6,   glowScale: 3.2, label: 'Fekete lyuk' },
  binary:       { core: 0xfff2b0, glow: 0xffcc02, radius: 5.5, glowScale: 3.2, label: 'Kettőscsillag' },
};

/** Fallback for an unexpected star type coming from the server. */
export const DEFAULT_STAR_STYLE: StarStyle = STAR_STYLES.yellow_dwarf;
export const starStyle = (t: string): StarStyle => (STAR_STYLES as Record<string, StarStyle>)[t] ?? DEFAULT_STAR_STYLE;

/** `#rrggbb` → number; tolerant of bad input (falls back to neutral grey). */
export function hexOf(css: string | undefined | null, fallback = 0x94a3b8): number {
  if (!css) return fallback;
  const m = /^#?([0-9a-f]{6})$/i.exec(css.trim());
  return m ? parseInt(m[1]!, 16) : fallback;
}

export const cssOf = (hex: number): string => `#${hex.toString(16).padStart(6, '0')}`;

/** Mixes two colours (t = 0 → a, 1 → b). */
export function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
  const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  const k = Math.max(0, Math.min(1, t));
  return (Math.round(ar + (br - ar) * k) << 16) | (Math.round(ag + (bg - ag) * k) << 8) | Math.round(ab + (bb - ab) * k);
}
